// @ts-nocheck
import { DeleteOutline, DriveFileMoveOutlined, LowPriorityOutlined } from '@mui/icons-material'
import { Autocomplete, Box, CardActions, CardMedia, Popover, TextField, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateMainComponentCache } from 'src/api/main-component'
import { invalidatePositionCache } from 'src/api/positions'
import { useDeleteROVVideo, useUpdateROVVideo } from 'src/api/rov-videos'
import MoveROVVideoDialog from 'src/dialogs/rov/MoveROVVideoDialog'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { ROVVideoStatus } from 'src/enums'
import { IROVVideoCategory, IROVVideoMinimal, IROVVideoUpdate } from 'src/generated-types/rovvideo'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { ReactComponent as RovPlayButton } from '../assets/svg/RovPlayButton.svg'
import { DynamicTextTypography } from './DynamicTextTypography'
import { getRovVideoContentVariant, shouldFetchVimeoOEmbed } from './ROVVideoComponent.action'
import {
  PlayButtonIconBox,
  PlayButtonOverlay,
  PopoverContentBox,
  ThumbnailBox,
  ThumbnailImage,
  TranscodingBox,
  VideoCardBox,
  VideoContainerBox,
  VideoContentBox,
  VideoInfoBox,
} from './ROVVideoComponent.style'
import { ROVVideoDalog } from './ROVVideoDalog'
import { Spinner } from './Spinner'

interface ROVVideoComponentProps {
  rovvideo: IROVVideoMinimal
  width?: number | string
  categories: IROVVideoCategory[]
  isShowAction?: boolean
  customerId: EntityId | null | undefined
  localityId: EntityId | null | undefined
  mooringId: EntityId | null | undefined
  positionId: EntityId | null | undefined
  mainComponentId: EntityId | null | undefined
  rowFlex?: string
  isFolderContainer?: boolean
  disabled?: boolean
}
export const ROVVideoComponent = ({
  rovvideo,
  width,
  categories,
  isShowAction,
  customerId,
  localityId,
  mooringId,
  positionId,
  mainComponentId,
  rowFlex,
  isFolderContainer = true,
  disabled = false,
}: ROVVideoComponentProps) => {
  const { showDialog, showWarningDialog } = useDialogCtx()
  const [isHovered, setIsHovered] = useState(false)
  const [thumbnailUrl, setThumbnailUrl] = useState(rovvideo.azureThumbnailURL ?? rovvideo.thumbnail_URL)
  const updateROVVideoMutation = useUpdateROVVideo(customerId as EntityId, localityId as EntityId, rovvideo.id)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const { mutateAsync: deleteROVVideoMutation } = useDeleteROVVideo(customerId as EntityId, localityId as EntityId)
  const { t } = useTranslation(['rov'])
  const theme = useTheme()

  useEffect(() => {
    if (shouldFetchVimeoOEmbed(rovvideo)) {
      fetch(`https://vimeo.com/api/oembed.json?url=https://player.vimeo.com/video/${rovvideo.vimeoId}`)
        .then(res => res.json())
        .then(data => setThumbnailUrl(data.thumbnail_url_with_play_button))
        .catch(() => {})
    } else {
      setThumbnailUrl(rovvideo.azureThumbnailURL ?? rovvideo.thumbnail_URL)
    }
  }, [rovvideo.azureBlobURL, rovvideo.azureThumbnailURL, rovvideo.thumbnail_URL, rovvideo.vimeoId])

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setAnchorPosition({
      top: rect.top,
      left: rect.right + 8,
    })
  }

  const handleClose = () => {
    setAnchorPosition(null)
  }

  const open = Boolean(anchorPosition)

  const onClickVimeoVideo = React.useCallback(async () => {
    const added = await showDialog(
      ROVVideoDalog,
      {
        componentProps: {
          rovvideo: rovvideo as IROVVideoMinimal,
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        title: rovvideo.title,
      },
      undefined,
      false,
    )
    if (!added) return
  }, [showDialog, rovvideo])

  // Moving a video that was uploaded on the wrong position or equipment.
  const handleMoveVimeoVideo = React.useCallback(async () => {
    await showDialog(
      MoveROVVideoDialog,
      {
        componentProps: {
          rovvideo: rovvideo as IROVVideoMinimal,
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          mooringId,
          positionId,
          mainComponentId,
        },
        opts: { maxWidth: 'xs', fullWidth: true },
        title: t('moveVideo'),
      },
      undefined,
      false,
    )
  }, [showDialog, rovvideo, customerId, localityId, mooringId, positionId, mainComponentId, t])

  const handleDeleteVimeoVideo = React.useCallback(
    async (rovvideo: IROVVideoMinimal) => {
      await showWarningDialog(t('deleteVideo'), t('areYouSureDeleteVideo'), true, [
        {
          text: t('delete', { ns: 'common' }),
          variant: 'contained',
          sx: {
            bgcolor: theme.palette.errorMain[theme.palette.mode],
            ':hover': { bgcolor: theme.palette.errorLight[theme.palette.mode] },
          },
          callback: async () => {
            await deleteROVVideoMutation(rovvideo.id)
            toast.success(t('deletedSuccessfully', { ns: 'common' }))
            invalidatePositionCache.getPositionROVVideos(
              customerId as EntityId,
              localityId as EntityId,
              mooringId as EntityId,
              positionId as EntityId,
            )
            invalidateMainComponentCache.getMainComponentROVVideos(customerId as EntityId, mainComponentId as EntityId)
            invalidationActivityLogCache.getMainComponentActivityLogs({
              mainComponentId: mainComponentId,
            })
            invalidationActivityLogCache.getCageActivityLogs({ positionId: positionId as EntityId })
          },
        },
      ])
    },
    [
      showWarningDialog,
      t,
      customerId,
      localityId,
      mooringId,
      positionId,
      mainComponentId,
      deleteROVVideoMutation,
      theme,
    ],
  )

  const renderVideoContent = () => {
    const variant = getRovVideoContentVariant(rovvideo, thumbnailUrl)

    if (variant === 'transcoding') {
      return (
        <TranscodingBox backgroundColor={getThemeColor(theme, MUIThemeColors.backgroundMain)}>
          <Spinner size={25} />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
              px: 1,
            }}
          >
            {t('videoProcessing', { ns: 'rov' })}
          </Typography>
        </TranscodingBox>
      )
    }

    if (variant === 'thumbnail') {
      return (
        <ThumbnailBox backgroundColor={getThemeColor(theme, MUIThemeColors.backgroundMain)}>
          <ThumbnailImage src={thumbnailUrl} alt={rovvideo.title || 'ROV Video'} />
        </ThumbnailBox>
      )
    }

    if (variant === 'blob-placeholder') {
      return <VideoContentBox backgroundColor={getThemeColor(theme, MUIThemeColors.backgroundMain)} />
    }

    // Fallback to iframe if no thumbnail available
    return (
      <VideoContentBox backgroundColor={getThemeColor(theme, MUIThemeColors.backgroundMain)}>
        <CardMedia
          component="iframe"
          src={`${process.env.REACT_APP_VIMEO_URL}/${rovvideo.vimeoId}&title=0&controls=0&byline=0&portrait=0`}
          title={rovvideo.title || 'ROV Video'}
          allowFullScreen
          sx={{
            aspectRatio: '16/9',
            width: '100%',
            height: '100%',
            border: 0,
            pointerEvents: 'none',
          }}
        />
      </VideoContentBox>
    )
  }

  return (
    <VideoContainerBox isFolderContainer={isFolderContainer} style={{ flex: rowFlex }}>
      <VideoCardBox
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        borderColor={getThemeColor(theme, MUIThemeColors.secondaryLight)}
        isTranscoding={rovvideo.status === ROVVideoStatus.Transcoding}
      >
        <Box onClick={() => rovvideo.status !== ROVVideoStatus.Transcoding && onClickVimeoVideo()}>
          {renderVideoContent()}
        </Box>
        {isShowAction === true && isHovered && (
          <CardActions
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              borderRadius: 1,
              padding: '0px 0px',
            }}
          >
            <GridActionButton
              toolTipTitle={t('moveToCategory')}
              width={isFolderContainer ? '24px' : '15px'}
              height={isFolderContainer ? '24px' : '15px'}
              buttonIcon={<DriveFileMoveOutlined sx={{ fontSize: '14px', color: 'white' }} />}
              onClick={e => {
                e.stopPropagation()
                handleOpen(e)
              }}
              disabled={disabled}
            />
            <GridActionButton
              toolTipTitle={t('moveVideo')}
              width={isFolderContainer ? '24px' : '15px'}
              height={isFolderContainer ? '24px' : '15px'}
              buttonIcon={<LowPriorityOutlined sx={{ fontSize: '14px', color: 'white' }} />}
              onClick={e => {
                e.stopPropagation()
                handleMoveVimeoVideo()
              }}
              disabled={disabled}
            />
            <GridActionButton
              toolTipTitle={t('deleteVideo')}
              width={isFolderContainer ? '24px' : '15px'}
              height={isFolderContainer ? '24px' : '15px'}
              sx={{ marginLeft: '0 !important' }}
              buttonIcon={
                <DeleteOutline
                  sx={{
                    fontSize: '16px',
                    color: 'white',
                  }}
                />
              }
              onClick={e => {
                e.stopPropagation()
                handleDeleteVimeoVideo(rovvideo)
              }}
              disabled={disabled}
            />
          </CardActions>
        )}

        {rovvideo.status !== ROVVideoStatus.Transcoding && (
          <PlayButtonOverlay>
            <PlayButtonIconBox
              onClick={e => {
                e.stopPropagation()
                onClickVimeoVideo()
              }}
            >
              <RovPlayButton />
            </PlayButtonIconBox>
          </PlayButtonOverlay>
        )}

        <Popover
          open={open}
          anchorReference="anchorPosition"
          anchorPosition={anchorPosition!}
          onClose={handleClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <PopoverContentBox>
            <Autocomplete<IROVVideoCategory>
              options={categories}
              getOptionLabel={option => option.categoryName}
              renderInput={params => <TextField {...params} label={t('selectFolder')} size="small" />}
              onChange={(_, newValue) => {
                if (newValue) {
                  const updateData: IROVVideoUpdate = {
                    id: rovvideo.id,
                    category: newValue,
                  }
                  updateROVVideoMutation.mutateAsync(updateData).then(() => {
                    invalidatePositionCache.getPositionROVVideos(
                      customerId as EntityId,
                      localityId as EntityId,
                      mooringId as EntityId,
                      positionId as EntityId,
                    )
                    invalidateMainComponentCache.getMainComponentROVVideos(
                      customerId as EntityId,
                      mainComponentId as EntityId,
                    )
                    invalidationActivityLogCache.getMainComponentActivityLogs({
                      mainComponentId: mainComponentId,
                    })
                    invalidationActivityLogCache.getCageActivityLogs({ positionId: positionId as EntityId })
                    toast.success(t('updatedSuccessfully', { ns: 'common' }))
                  })

                  handleClose()
                }
              }}
            />
          </PopoverContentBox>
        </Popover>

        <VideoInfoBox>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: getThemeColor(theme, MUIThemeColors.primaryDark),
              letterSpacing: '-0.0762px',
              lineHeight: '19.5px',
            }}
          >
            <DynamicTextTypography
              lineHeight={1.5}
              fontWeight={600}
              fontSize={13}
              maxLength={18}
              text={rovvideo.title}
              sx={{ pointerEvents: 'auto' }}
            />
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 400,
              color: getThemeColor(theme, MUIThemeColors.secondaryMain),
              letterSpacing: '0.0645px',
              lineHeight: '16.5px',
            }}
          >
            <DynamicTextTypography
              lineHeight={1.5}
              fontWeight={400}
              fontSize={11}
              maxLength={18}
              color={getThemeColor(theme, MUIThemeColors.secondaryMain)}
              text={getFormattedDate(rovvideo?.createdAt)}
              sx={{ pointerEvents: 'auto' }}
            />
          </Typography>
        </VideoInfoBox>
      </VideoCardBox>
    </VideoContainerBox>
  )
}
